import OfflineStore from "./OfflineStore";
import PrecacheIndex from "./PrecacheIndex";
import OfflineErrors from "./OfflineErrors";
import { isPrecachePrimary, checkStaleness } from "./PrecacheConfig";
import SyncScheduler from "./SyncScheduler";
import Item_Price_Fetcher from "./Item_Price_Fetcher";
import Item_Inventory_Fetcher from "./Item_Inventory_Fetcher";

/**
 * Offline Interceptor — registers on a LiveApiClient (axios) instance.
 *
 * For models with OFFLINE=true:
 *   - On success: caches the response in IndexedDB
 *   - On network failure (no response):
 *     - Reads: serves cached response
 *     - Writes: queues mutation in sync_queue, returns optimistic response
 *
 * For models with CACHE_FIRST=true:
 *   - Serves fresh cached response immediately (skips network)
 *   - Respects the "nocache" localStorage flag (preview mode)
 *
 * Performance optimizations:
 *   - Request deduplication: identical in-flight requests share the same promise
 *   - Stale-while-revalidate: serves stale cache immediately, updates in background
 *
 * Models opt in by setting _model on the axios config:
 *   config._model = { className, offline, cacheFirst, cacheTTL }
 *
 * Automatic detection: If _model is not set, the interceptor will try to
 * infer the model from the URL pattern and apply appropriate caching.
 */
export default class OfflineInterceptor {
	static #tempIdCounter = 0;
	static #installed = false;
	static #axiosInstance = null;

	// On-demand data fetcher instances (lazy-initialized, reused across requests)
	static #priceFetcher = null;
	static #inventoryFetcher = null;

	// Request deduplication: track in-flight requests
	static #inFlightRequests = new Map();
	static #DEDUP_TIMEOUT = 100; // ms to wait for identical requests

	/**
	 * Purge stale entries from the in-flight request map to prevent unbounded growth.
	 */
	static #cleanupInflight() {
		const now = Date.now();
		for (const [key, entry] of this.#inFlightRequests) {
			if (now - entry.startTime > 5000) {
				this.#inFlightRequests.delete(key);
			}
		}
	}

	/**
	 * URL pattern to model config mapping.
	 * Used when requests don't explicitly set _model.
	 */
	static #urlPatterns = {
		// Items and products (catalog page uses /records endpoint)
		'Item': { className: 'Item', offline: true, cacheFirst: true, cacheTTL: 21600 },
		'records': { className: 'Item', offline: true, cacheFirst: true, cacheTTL: 21600 },
		'count': { className: 'Item', offline: true, cacheFirst: true, cacheTTL: 21600 },
		// Assets
		'Asset': { className: 'Asset', offline: true, cacheFirst: true, cacheTTL: 21600 },
		// Filters and attributes
		'attributes': { className: 'Attributes', offline: true, cacheFirst: true, cacheTTL: 86400 },
		// Catalog structure
		'Catalog': { className: 'Catalog', offline: true, cacheFirst: false, cacheTTL: 14400 },
		'Catalog_Invitation': { className: 'CatalogInvitation', offline: true, cacheFirst: false, cacheTTL: 14400 },
		'Catalog_Organization_Access': { className: 'CatalogOrganizationAccess', offline: true, cacheFirst: false, cacheTTL: 14400 },
		// Module sets (page layouts)
		'Module_Set': { className: 'ModuleSet', offline: true, cacheFirst: true, cacheTTL: 86400 },
		// Forms
		'Catalog_Form': { className: 'CatalogForm', offline: true, cacheFirst: true, cacheTTL: 86400 },
		// Reference data
		'Payment_Method': { className: 'PaymentMethod', offline: true, cacheFirst: true, cacheTTL: 86400 },
		'Shipping_Method': { className: 'ShippingMethod', offline: true, cacheFirst: true, cacheTTL: 86400 },
		'Purchase_Order_Type': { className: 'PurchaseOrderType', offline: true, cacheFirst: true, cacheTTL: 86400 },
		'Organization_Member_Role': { className: 'OrganizationMemberRole', offline: true, cacheFirst: true, cacheTTL: 86400 },
		// User data (shorter TTL, not cache-first)
		'Proposal': { className: 'Proposal', offline: true, cacheFirst: true, cacheTTL: 3600 },
		'Proposal_Item': { className: 'ProposalItem', offline: true, cacheFirst: false, cacheTTL: 3600 },
		'Item_Bookmark': { className: 'BookmarkItem', offline: true, cacheFirst: false, cacheTTL: 3600 },
		'Customer': { className: 'Customer', offline: true, cacheFirst: true, cacheTTL: 21600 },
	};

	/**
	 * Check if URL is a records endpoint (catalog items).
	 */
	static #isRecordsEndpoint(url) {
		if (!url) return false;
		return url.includes('/records') || url.startsWith('records');
	}

	/**
	 * Check if URL is a count endpoint.
	 */
	static #isCountEndpoint(url) {
		if (!url) return false;
		return /\/count(\?|$)/.test(url) || url.endsWith('/count');
	}

	/**
	 * Check if URL is a details endpoint (single item details).
	 */
	static #isDetailsEndpoint(url) {
		if (!url) return false;
		return url.includes('/attributes/details') || url.includes('/details?item=');
	}

	/**
	 * Check if URL is a gallery attributes endpoint (not details).
	 * This is the endpoint that returns attribute definitions for the gallery/thumbnail view.
	 */
	static #isGalleryAttributesEndpoint(url) {
		if (!url) return false;
		// Match /attributes at the end or /attributes? but NOT /attributes/details
		return (url.endsWith('/attributes') || url.includes('/attributes?'))
			&& !url.includes('/attributes/details');
	}

	/**
	 * Check if URL is a filters endpoint.
	 * This returns filter definitions with options and counts.
	 */
	static #isFiltersEndpoint(url) {
		if (!url) return false;
		return url.endsWith('/filters') || url.includes('/filters?');
	}

	/**
	 * Check if URL is the precache endpoint (used for offline sync).
	 * This endpoint should NEVER be intercepted - it needs to hit the network to download data.
	 */
	static #isPrecacheEndpoint(url) {
		if (!url) return false;
		return url.includes('/precache');
	}

	/**
	 * Extract catalog ID from request config.
	 * Looks in URL params, headers, or tries to get from OfflineStore.
	 * @returns {string|number|null}
	 */
	static #extractCatalogId(config) {
		// Try URL params (e.g., ?catalog=123 or ?page=123)
		const params = config.params || {};
		if (params.catalog) return params.catalog;
		if (params.page) {
			// Page ID might be in the URL, catalog ID would be in OfflineStore
			// Fall through to OfflineStore check
		}

		// Try to extract from URL path (e.g., /Catalog_Page/123/records)
		const url = config.url || "";
		const pageMatch = url.match(/Catalog_Page\/(\d+)/);
		if (pageMatch) {
			// We have a page ID - the catalog should already be set in OfflineStore
			// Return null to use the current OfflineStore catalog
			return OfflineStore.GetCurrentCatalogId?.() || null;
		}

		const catalogMatch = url.match(/Catalog\/(\d+)/);
		if (catalogMatch) {
			return catalogMatch[1];
		}

		// Fallback: OfflineStore should have the current catalog set
		return OfflineStore.GetCurrentCatalogId?.() || null;
	}

	/**
	 * Check if URL is an /Asset/ endpoint (asset list query).
	 */
	static #isAssetEndpoint(url) {
		if (!url) return false;
		return url.match(/^\/Asset\/?$/i) || url.match(/^Asset\/?$/i) || url.includes('/Asset?');
	}

	/**
	 * Check if URL is a single item endpoint (e.g., /Item/12345).
	 * Returns the item ID if matched, null otherwise.
	 */
	static #getSingleItemId(url) {
		if (!url) return null;
		// Match /Item/12345 or Item/12345 patterns
		const match = url.match(/\/Item\/(\d+)|^Item\/(\d+)/i);
		return match ? (match[1] || match[2]) : null;
	}

	/**
	 * Check if this is a global search request (/Item?q=...).
	 * @param {object} config - Axios request config
	 * @returns {string|null} The search query or null
	 */
	static #getGlobalSearchQuery(config) {
		if (!config.url) return null;
		// Only handle /Item endpoint (not /Item/123)
		if (!config.url.match(/^\/Item\/?$/i) && !config.url.match(/^Item\/?$/i)) {
			return null;
		}
		// Check for 'q' param (global search query)
		return config.params?.q || null;
	}

	/**
	 * Check if this is an /Item request and extract item IDs from filters param.
	 * ProductUtil.GetProduct uses: GET /Item?filters=[[{"property":"id","value":12345}]]
	 * @returns {Array<number>|null} Array of item IDs or null
	 */
	static #getItemIdsFromFilters(config) {
		if (!config.url) return null;
		// Only handle /Item endpoint (not /Item/123 which is handled separately)
		if (!config.url.match(/^\/Item\/?$/i) && !config.url.match(/^Item\/?$/i)) {
			return null;
		}

		const filtersParam = config.params?.filters;
		if (!filtersParam) return null;

		try {
			const filters = typeof filtersParam === 'string' ? JSON.parse(filtersParam) : filtersParam;
			// Format is [[{property: "id", value: 12345}, ...]] - array of filter groups (OR)
			// Each group contains filters (AND within group)
			const itemIds = [];
			for (const filterGroup of filters) {
				if (!Array.isArray(filterGroup)) continue;
				for (const filter of filterGroup) {
					if (filter.property === 'id' && filter.value) {
						itemIds.push(filter.value);
					}
				}
			}
			return itemIds.length > 0 ? itemIds : null;
		} catch (e) {
			console.warn('[OfflineInterceptor] Failed to parse filters param:', e);
			return null;
		}
	}

	/**
	 * Handle GET requests in simulated offline mode.
	 * Always returns a config with adapter set - never lets requests through to network.
	 */
	static async #handleOfflineGetRequest(config, store) {
		const isRecords = this.#isRecordsEndpoint(config.url);
		const isDetails = this.#isDetailsEndpoint(config.url);
		const isGalleryAttributes = this.#isGalleryAttributesEndpoint(config.url);
		const isFilters = this.#isFiltersEndpoint(config.url);
		const isCount = this.#isCountEndpoint(config.url);
		const singleItemId = this.#getSingleItemId(config.url);

		// Special handling for records endpoint (catalog items) - use precache with filtering
		if (isRecords) {
			const precacheResult = await this.#servePrecachedRecords(config, store);

			// For count endpoints, return just the total count
			if (isCount && precacheResult) {
				const countResult = precacheResult._total ?? precacheResult.data?.length ?? 0;
				config.adapter = () => Promise.resolve({
					data: countResult,
					status: 200,
					statusText: "OK",
					headers: {},
					config,
					_fromCache: true,
					_fromPrecache: true,
				});
				return config;
			}

			if (precacheResult) {
				config.adapter = () => Promise.resolve(precacheResult);
				return config;
			}
		}

		// Special handling for details endpoint (single item) - serve from precache
		if (isDetails) {
			const detailsResult = await this.#servePrecachedDetails(config, store);
			if (detailsResult) {
				config.adapter = () => Promise.resolve(detailsResult);
				return config;
			}
		}

		// Special handling for gallery attributes endpoint - serve from cache or build fallback
		if (isGalleryAttributes) {
			const galleryAttrsResult = await this.#servePrecachedGalleryAttributes(config, store);
			if (galleryAttrsResult) {
				config.adapter = () => Promise.resolve(galleryAttrsResult);
				return config;
			}
		}

		// Special handling for filters endpoint - serve from cache with dynamic counts
		if (isFilters) {
			try {
				const filtersResult = await this.#servePrecachedFilters(config, store);
				if (filtersResult) {
					config.adapter = () => Promise.resolve(filtersResult);
					return config;
				}
			} catch (e) {
				console.warn('[OfflineInterceptor] Error serving precached filters in offline mode:', e);
			}
		}

		// Handle single item lookup (e.g., /Item/12345)
		if (singleItemId) {
			const itemResult = await this.#servePrecachedSingleItem(singleItemId, store);
			if (itemResult) {
				config.adapter = () => Promise.resolve(itemResult);
				return config;
			}
		}

		// Handle global search (/Item?q=...) - the CatalogSearch modal
		const globalSearchQuery = this.#getGlobalSearchQuery(config);
		if (globalSearchQuery) {
			const searchResult = await this.#servePrecachedGlobalSearch(config, store);
			if (searchResult) {
				config.adapter = () => Promise.resolve(searchResult);
				return config;
			}
		}

		// Handle /Item requests with item ID in filters param (e.g., ProductUtil.GetProduct)
		const filterItemIds = this.#getItemIdsFromFilters(config);
		if (filterItemIds && filterItemIds.length > 0) {
			const items = [];
			for (const itemId of filterItemIds) {
				const itemResult = await this.#servePrecachedSingleItem(itemId, store);
				if (itemResult?.data) {
					items.push(itemResult.data);
				}
			}
			if (items.length > 0) {
				config.adapter = () => Promise.resolve({
					data: items,
					status: 200,
					statusText: "OK",
					headers: {},
					config,
					_fromCache: true,
					_fromPrecache: true,
				});
				return config;
			}
		}

		// Handle /Asset/ requests - serve from precached assets
		if (this.#isAssetEndpoint(config.url)) {
			const assetResult = await this.#servePrecachedAssets(config, store);
			if (assetResult) {
				config.adapter = () => Promise.resolve(assetResult);
				return config;
			}
		}

		// Handle standalone count endpoints (not under /records)
		if (isCount && !isRecords) {
			const precacheResult = await this.#servePrecachedRecords(config, store);
			if (precacheResult) {
				const countResult = precacheResult._total ?? precacheResult.data?.length ?? 0;
				config.adapter = () => Promise.resolve({
					data: countResult,
					status: 200,
					statusText: "OK",
					headers: {},
					config,
					_fromCache: true,
					_fromPrecache: true,
				});
				return config;
			}
		}

		// Try generic cache lookup
		const key = this.#getDedupKey(config);
		const cached = store
			? await store.GetCachedResponseWithMeta(key, Infinity)
			: await OfflineStore.GetCachedResponseWithMeta(key, Infinity);

		if (cached) {
			config.adapter = () => Promise.resolve({
				data: cached.data,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
			});
			return config;
		}

		// FALLBACK: No cache available - return empty array to prevent network request
		config.adapter = () => Promise.resolve({
			data: [],
			status: 200,
			statusText: "OK",
			headers: {},
			config,
			_fromCache: true,
			_fromPrecache: true,
			_emptyFallback: true,
		});
		return config;
	}

	/**
	 * Infer model config from URL when _model is not explicitly set.
	 */
	static #inferModelFromUrl(url) {
		if (!url) return null;

		// Check for catalog-specific endpoints first (e.g., :accountHandle:catalogHandle/records or records?...)
		if (this.#isRecordsEndpoint(url)) {
			return this.#urlPatterns['records'];
		}

		// Check for count endpoints (including catalog page count like Catalog_Page/123/count)
		if (this.#isCountEndpoint(url)) {
			return this.#urlPatterns['count'];
		}

		// Check for attribute detail endpoints (e.g., Catalog_Page/123/attributes/details)
		// This must come BEFORE the gallery attributes check
		if (this.#isDetailsEndpoint(url)) {
			return { className: 'AttributeDetails', offline: true, cacheFirst: true, cacheTTL: 86400 };
		}

		// Check for gallery attribute endpoints (e.g., Catalog_Page/123/attributes)
		if (this.#isGalleryAttributesEndpoint(url)) {
			return { className: 'GalleryAttributes', offline: true, cacheFirst: true, cacheTTL: 86400 };
		}

		// Check for known patterns in the URL
		for (const [pattern, config] of Object.entries(this.#urlPatterns)) {
			// Match /Pattern/ or /Pattern? or /Pattern at end, or Pattern at start
			const regex = new RegExp(`(?:^|[/])${pattern}(?:[/?]|$)`, 'i');
			if (regex.test(url)) {
				return config;
			}
		}

		return null;
	}

	/**
	 * Get the model config, either from explicit _model or inferred from URL.
	 */
	static #getModelConfig(config) {
		if (config._model) return config._model;
		return this.#inferModelFromUrl(config.url);
	}

	/**
	 * Generate a deduplication key for a request.
	 */
	static #getDedupKey(config) {
		// Include account header to prevent cross-catalog dedup collisions
		const account = config.headers?.account || '';
		const baseKey = OfflineStore.GenerateKey(config.method, config.url, config.params);
		return account ? `${account}:${baseKey}` : baseKey;
	}

	/**
	 * Install interceptors on the given axios instance.
	 * Safe to call multiple times — only installs once.
	 * @param {import("axios").AxiosInstance} axiosInstance
	 */
	static Install(axiosInstance) {
		if (this.#installed) return;
		this.#installed = true;
		this.#axiosInstance = axiosInstance;

		// Request interceptor: CACHE_FIRST short-circuit + deduplication
		axiosInstance.interceptors.request.use(
			config => this.#onRequest(config),
			error => Promise.reject(error)
		);

		// Response interceptor: cache on success, offline fallback on failure
		axiosInstance.interceptors.response.use(
			response => this.#onSuccess(response),
			error => this.#onError(error)
		);
	}

	/**
	 * Request interceptor: check CACHE_FIRST for GET requests.
	 * Implements:
	 *   - Request deduplication (same request in-flight returns same promise)
	 *   - Cache-first (fresh cache skips network)
	 *   - Stale-while-revalidate (stale cache served immediately, network updates in background)
	 *   - Simulated offline mode (forces cache-only, blocks network)
	 */
	static async #onRequest(config) {
		const model = this.#getModelConfig(config);
		const isSimulatedOffline = OfflineStore.IsSimulatingOffline();
		const method = config.method?.toLowerCase();
		const catalogId = this.#extractCatalogId(config);
		const store = catalogId ? OfflineStore.ForCatalog(catalogId) : null;


		// CRITICAL: In simulated offline mode, NEVER let GET requests through to network
		// This is the master guard - any GET request must be served from cache or return empty
		// EXCEPTION: Precache endpoint MUST hit network to download fresh data for sync
		if (isSimulatedOffline && method === "get") {
			// Allow precache endpoint through to network for sync downloads
			if (this.#isPrecacheEndpoint(config.url)) {
				return config; // Let it through to network
			}

			try {
				return await this.#handleOfflineGetRequest(config, store);
			} catch (e) {
				console.warn(`[OfflineInterceptor] Error handling offline request, returning empty:`, e);
				config.adapter = () => Promise.resolve({
					data: [],
					status: 200,
					statusText: "OK",
					headers: {},
					config,
					_fromCache: true,
					_fromPrecache: true,
					_errorFallback: true,
				});
				return config;
			}
		}

		// For offline write requests (simulated or real), queue them immediately
		// This prevents writes from hanging on network timeout when truly offline
		if ((isSimulatedOffline || !navigator.onLine) && method !== "get") {
			if (model?.offline) {
				return this.#queueOfflineWrite(config, model, store);
			}
			// Block non-offline writes in offline mode
			config.adapter = () => Promise.reject(new Error('Offline mode: write operations not available'));
			return config;
		}

		// ─── CACHE-PRIMARY MODE ────────────────────────────────────
		// Check precache status once per request (avoids 5-8 redundant IDB calls)
		// Skip precache for background revalidation requests (they must hit network)
		const hasPrecache = (method === "get" && catalogId && !config._skipPrecache)
			? await isPrecachePrimary(catalogId)
			: false;

		// For /records requests, serve from precache if available (even when online)
		// This provides instant UI from IndexedDB, with background delta sync for freshness
		if (method === "get" && this.#isRecordsEndpoint(config.url)) {
			if (!catalogId) {
			} else {
				if (!hasPrecache) {
				} else {
					const precacheResult = await this.#servePrecachedRecords(config, store);
					if (!precacheResult) {
					}
					if (precacheResult) {
						// Check staleness and trigger background sync if needed
						const { isStale } = await checkStaleness(catalogId);
						if (isStale && !SyncScheduler.IsSyncing()) {
							// Fire and forget - don't block the response
							SyncScheduler.TriggerSync().catch(e => OfflineErrors.log("TriggerSync", e));
						}

						// For /records/count, return just the total number
						if (this.#isCountEndpoint(config.url)) {
							const countResult = precacheResult._total ?? precacheResult.data?.length ?? 0;
							config.adapter = () => Promise.resolve({
								data: countResult,
								status: 200,
								statusText: "OK",
								headers: {},
								config,
								_fromCache: true,
								_fromPrecache: true,
							});
							config._fromPrecache = true;
							return config;
						}

						config.adapter = () => Promise.resolve(precacheResult);
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// For global search (/Item?q=...), serve from precache if available (even when online)
		// This provides instant search results from IndexedDB
		const globalSearchQuery = this.#getGlobalSearchQuery(config);
		if (method === "get" && globalSearchQuery) {
			if (catalogId) {
				if (hasPrecache) {
					const searchResult = await this.#servePrecachedGlobalSearch(config, store);
					if (searchResult) {
						config.adapter = () => Promise.resolve(searchResult);
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// For gallery attributes (/attributes), serve from precache fields if available
		// This ensures thumbnail display works even if the /attributes endpoint is misconfigured
		if (method === "get" && this.#isGalleryAttributesEndpoint(config.url)) {
			if (catalogId) {
				if (hasPrecache) {
					const attrsResult = await this.#servePrecachedGalleryAttributes(config, store);
					if (attrsResult) {
						config.adapter = () => Promise.resolve(attrsResult);
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// For detail attributes (/attributes/details?item=X), serve from precache
		if (method === "get" && this.#isDetailsEndpoint(config.url)) {
			if (catalogId) {
				if (hasPrecache) {
					const detailsResult = await this.#servePrecachedDetails(config, store);
					if (detailsResult) {
						config.adapter = () => Promise.resolve(detailsResult);
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// For /Item?filters=[[{property:"id",value:X}]] (product detail lookups), serve from precache
		if (method === "get") {
			const filterItemIds = this.#getItemIdsFromFilters(config);
			if (filterItemIds && filterItemIds.length > 0 && catalogId) {
				if (hasPrecache) {
					const items = [];
					for (const itemId of filterItemIds) {
						const itemResult = await this.#servePrecachedSingleItem(itemId, store);
						if (itemResult?.data) {
							items.push(itemResult.data);
						}
					}
					if (items.length > 0) {
						config.adapter = () => Promise.resolve({
							data: items,
							status: 200,
							statusText: "OK",
							headers: {},
							config,
							_fromCache: true,
							_fromPrecache: true,
						});
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// For /Item/12345 (single item by ID), serve from precache
		if (method === "get") {
			const singleItemId = this.#getSingleItemId(config.url);
			if (singleItemId && catalogId) {
				if (hasPrecache) {
					const itemResult = await this.#servePrecachedSingleItem(singleItemId, store);
					if (itemResult) {
						config.adapter = () => Promise.resolve(itemResult);
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// For filters endpoint in cache-primary mode, serve from cache if we have
		// cached filters with values. This avoids redundant API calls when filter
		// selections change - counts are recalculated client-side from precached items.
		if (method === "get" && this.#isFiltersEndpoint(config.url)) {
			if (catalogId) {
				if (hasPrecache) {
					const filtersResult = await this.#servePrecachedFilters(config, store);
					if (filtersResult) {
						config.adapter = () => Promise.resolve(filtersResult);
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// For filters endpoint without precache, let the API request go through.
		// The #onSuccess handler will cache the full filter definitions and recalculate
		// counts from precached items. This ensures we get the complete filter set
		// (not just the bare filter_defs from the precache endpoint).
		// Offline mode handles filters separately in #handleOfflineGetRequest.

		// For /Asset/ requests, serve from precache if available (even when online)
		// This provides instant asset loading for the gallery
		if (method === "get" && this.#isAssetEndpoint(config.url)) {
			if (catalogId) {
				if (hasPrecache) {
					const assetResult = await this.#servePrecachedAssets(config, store);
					if (assetResult) {
						config.adapter = () => Promise.resolve(assetResult);
						config._fromPrecache = true;
						return config;
					}
				}
			}
		}

		// ─── CONTEXT-AWARE CACHE-FIRST ────────────────────────────────
		// Catalog_Invitation: use cache-first when in a catalog context (private layout)
		// Once inside a catalog, invitation data is unlikely to change
		if (method === "get" && model?.className === 'CatalogInvitation' && catalogId) {
			const key = this.#getDedupKey(config);
			const ttlMs = (model.cacheTTL || 14400) * 1000;
			const cached = store
				? await store.GetCachedResponseWithMeta(key, ttlMs)
				: await OfflineStore.GetCachedResponseWithMeta(key, ttlMs);

			if (cached && !cached.isStale) {
				config.adapter = () => Promise.resolve({
					data: cached.data,
					status: 200,
					statusText: "OK",
					headers: {},
					config,
					_fromCache: true,
				});
				return config;
			}
		}

		// Online mode - normal cache-first handling
		if (!model?.cacheFirst) return config;
		if (method !== "get") return config;
		if (localStorage.getItem("nocache")) return config;

		// Store resolved model on config for later use
		config._model = model;

		const key = this.#getDedupKey(config);
		const ttlMs = (model.cacheTTL || 21600) * 1000;

		// Request deduplication: if same request is in-flight, wait for it
		if (this.#inFlightRequests.has(key)) {
			const existing = this.#inFlightRequests.get(key);
			// Only deduplicate if request is recent (within DEDUP_TIMEOUT)
			if (Date.now() - existing.startTime < this.#DEDUP_TIMEOUT) {
				config._deduped = true;
				config.adapter = () => existing.promise;
				return config;
			}
		}

		// Check cache with metadata for stale-while-revalidate
		const cached = store
			? await store.GetCachedResponseWithMeta(key, ttlMs)
			: await OfflineStore.GetCachedResponseWithMeta(key, ttlMs);

		if (cached) {
			if (!cached.isStale) {
				// Fresh cache: serve immediately, skip network
				config.adapter = () => Promise.resolve({
					data: cached.data,
					status: 200,
					statusText: "OK",
					headers: {},
					config,
					_fromCache: true,
				});
			} else {
				// Stale cache: serve immediately, trigger background revalidation
				const staleData = cached.data;
				config.adapter = () => {
					// Trigger background revalidation (fire and forget)
					this.#backgroundRevalidate(config.url, { ...config, _model: { ...model, cacheFirst: false } });
					return Promise.resolve({
						data: staleData,
						status: 200,
						statusText: "OK",
						headers: {},
						config,
						_fromCache: true,
						_stale: true,
					});
				};
			}
		}

		return config;
	}

	/**
	 * Response success handler: cache the response for OFFLINE-enabled models.
	 * Also caches individual items by ID for offline reconstruction.
	 */
	static async #onSuccess(response) {
		// Skip responses from cache (custom adapters) - they don't need caching
		if (response?._fromCache || response?._fromPrecache) return response;

		// Guard against responses without config (e.g., from custom adapters)
		if (!response?.config) return response;

		const model = this.#getModelConfig(response.config);
		const key = this.#getDedupKey(response.config);

		// Remove from in-flight tracking and clean up stale entries
		this.#inFlightRequests.delete(key);
		this.#cleanupInflight();

		// For /filters responses, store the full filter structure (with values)
		// in the precache filters cache. This overwrites the bare filter_defs from
		// the precache endpoint, enabling future requests to be served from cache
		// with client-side count calculation.
		// This runs before the model check because /filters has no model config.
		if (this.#isFiltersEndpoint(response.config.url) && Array.isArray(response.data)) {
			const catalogId = this.#extractCatalogId(response.config);
			const s = catalogId ? OfflineStore.ForCatalog(catalogId) : OfflineStore;
			s.CacheFilters(response.data);

			// Recalculate counts from precached items so the UI shows page-scoped
			// counts instead of catalog-wide counts from the API.
			// Pass response.data as filterDefsOverride since CacheFilters uses
			// batched writes that may not have flushed to IndexedDB yet.
			// IMPORTANT: merge recalculated counts INTO the API response rather
			// than replacing it. The recalc may prune options that the API returned
			// (e.g. when precache is still building or referenceMap is incomplete).
			// Preserve all API options; only update counts from the recalc.
			if (catalogId) {
				try {
					const hasPrecache = await isPrecachePrimary(catalogId);
					if (hasPrecache) {
						const recalcResult = await this.#servePrecachedFilters(response.config, s, response.data);
						if (recalcResult?.data && Array.isArray(recalcResult.data)) {
							const getKey = (f) => String(f?.reference ?? f?.property ?? f?.id ?? '');
							const recalcByKey = new Map(recalcResult.data.map(f => [getKey(f), f]));
							response.data = response.data.map(apiFilter => {
								const recalcFilter = recalcByKey.get(getKey(apiFilter));
								if (!recalcFilter?.values || !Array.isArray(apiFilter.values)) return apiFilter;
								const recalcCounts = new Map(
									recalcFilter.values.map(v => [String(v?.id), +v?.count || 0])
								);
								return {
									...apiFilter,
									values: apiFilter.values.map(opt => ({
										...opt,
										count: recalcCounts.has(String(opt?.id))
											? recalcCounts.get(String(opt?.id))
											: (opt.count ?? 0),
									})),
								};
							});
						}
					}
				} catch (e) {
					// If recalculation fails, keep the original API response
					console.warn('[OfflineInterceptor] Failed to recalculate filter counts from precache:', e);
				}
			}
		}

		if (!model?.offline) return response;

		// Get catalog-bound store for caching
		const catalogId = this.#extractCatalogId(response.config);
		const store = catalogId ? OfflineStore.ForCatalog(catalogId) : null;

		// Cache the response (fire-and-forget — errors are non-fatal)
		if (store) {
			store.CacheResponse(key, model.className, response.data).catch(() => {});
			store.SetLastSyncTime(model.className);
			if (Array.isArray(response.data) && response.data.length > 0) {
				store.CacheItems(model.className, response.data).catch(() => {});
			}
		} else {
			// Fallback to bridge (legacy behavior)
			OfflineStore.CacheResponse(key, model.className, response.data).catch(() => {});
			OfflineStore.SetLastSyncTime(model.className);
			if (Array.isArray(response.data) && response.data.length > 0) {
				OfflineStore.CacheItems(model.className, response.data).catch(() => {});
			}
		}

		return response;
	}

	/**
	 * Response error handler: serve cache or queue writes on network failure.
	 * Only intercepts when:
	 *   - There is no error.response (network failure, not server error)
	 *   - The request has _model.offline = true
	 */
	static async #onError(error) {
		const config = error.config;

		// Guard against errors without config
		if (!config) return Promise.reject(error);

		const model = this.#getModelConfig(config);
		const key = this.#getDedupKey(config);

		// Remove from in-flight tracking and clean up stale entries
		this.#inFlightRequests.delete(key);
		this.#cleanupInflight();

		// If we have stale data and network failed, serve stale
		if (config._staleWhileRevalidate && config._staleData) {
			return { data: config._staleData, status: 200, config, _fromCache: true, _stale: true };
		}

		// Pass through server errors (4xx/5xx) — server is reachable
		if (error.response) return Promise.reject(error);

		// Pass through if not an offline-enabled model
		if (!model?.offline) return Promise.reject(error);

		// Get catalog-bound store for cache lookups
		const catalogId = this.#extractCatalogId(config);
		const store = catalogId ? OfflineStore.ForCatalog(catalogId) : null;

		const method = config.method;

		// Read: serve from cache (ignore TTL — any data is better than nothing)
		if (method === "get") {
			// Try precache first for records endpoint (catalog items with search/filter support)
			if (this.#isRecordsEndpoint(config.url)) {
				const precacheResult = await this.#servePrecachedRecords(config, store);
				if (precacheResult) {
					return precacheResult;
				}
			}

			const cached = store
				? await store.GetCachedResponse(key)
				: await OfflineStore.GetCachedResponse(key);
			if (cached) {
				return { data: cached, status: 200, config, _fromCache: true };
			}

			// Fallback: try item-level cache for Item class
			if (model.className === "Item") {
				const limit = parseInt(config.params?.limit) || 60;
				const offset = parseInt(config.params?.offset) || 0;
				const { items } = store
					? await store.GetCachedItems("Item", limit, offset)
					: await OfflineStore.GetCachedItems("Item", limit, offset);
				if (items.length > 0) {
					return { data: items, status: 200, config, _fromCache: true, _fromItemCache: true };
				}
			}

			return Promise.reject(error);
		}

		// Write: queue the mutation and return optimistic response
		// Don't re-queue during replay — let OfflineSyncQueue handle the failure
		if (config._replay) {
			return Promise.reject(error);
		}

		if (store) {
			await store.QueueMutation(model.className, method, config.url, config.data, config);
		} else {
			await OfflineStore.QueueMutation(model.className, method, config.url, config.data, config);
		}

		const optimistic = this.#buildOptimisticResponse(method, config.data);
		return { data: optimistic, status: 200, config, _queued: true };
	}

	/**
	 * Build an optimistic response for a queued write.
	 * - POST (create): echoes payload with a negative temp ID
	 * - PUT (update): echoes payload as-is
	 * - DELETE: returns empty object
	 */
	static #buildOptimisticResponse(method, payload) {
		if (method === "delete") {
			return {};
		}

		const data = typeof payload === "string"
			? (() => { try { return JSON.parse(payload); } catch { return {}; } })()
			: { ...payload };

		if (method === "post" && !data.id) {
			data.id = --this.#tempIdCounter;
		}

		data._pending_sync = true;
		return data;
	}

	/**
	 * Serve records from precache with search/filter support.
	 * Used for offline catalog browsing.
	 */
	static async #servePrecachedRecords(config, store) {
		// Merge params from config.params and URL query string
		const params = { ...config.params };

		// Also parse URL query string in case params aren't in config.params
		if (config.url?.includes('?')) {
			const urlParams = new URLSearchParams(config.url.split('?')[1]);
			for (const [key, value] of urlParams.entries()) {
				if (!params[key]) params[key] = value;
			}
		}

		// Parse filter params - can be in 'filter', 'itemFilter', or 'filters' param
		// Format can be array of arrays: [["c.11",[88,130,131]]]
		// Or array of objects: [{"property":"c.12","value":[12]}]
		const filters = [];

		// Helper to parse filter values from various formats
		const parseFilterParam = (paramValue) => {
			try {
				const parsed = typeof paramValue === "string"
					? JSON.parse(paramValue)
					: paramValue;
				if (Array.isArray(parsed)) {
					for (const f of parsed) {
						// Handle array format: ["c.11", [88, 130, 131]]
						if (Array.isArray(f) && f.length >= 2) {
							const [property, value] = f;
							filters.push({ filterId: property, value });
						}
						// Handle object format: {property: "c.11", value: [88, 130, 131]}
						else if (f.property && f.value !== undefined) {
							filters.push({ filterId: f.property, value: f.value });
						}
					}
				}
			} catch (e) {
				console.warn("[OfflineInterceptor] Failed to parse filter param:", e);
			}
		};

		// Check 'filter' param
		if (params.filter) {
			parseFilterParam(params.filter);
		}

		// Check 'itemFilter' param (used by catalog page)
		if (params.itemFilter) {
			parseFilterParam(params.itemFilter);
		}

		// Also check 'filters' param (alternative format)
		if (params.filters) {
			parseFilterParam(params.filters);
		}

		// Check for filter params like filter_238011=BANGLES
		for (const [key, value] of Object.entries(params)) {
			if (key.startsWith("filter_") && value) {
				const filterId = key.replace("filter_", "");
				filters.push({ filterId, value });
			}
		}

		const search = params.search || params.q || "";
		const limit = parseInt(params.limit) || 60;
		const offset = parseInt(params.offset) || 0;
		const sort = params.sort || null;
		const group = params.group || null; // Grouping mode: 'common_sku', 'family', 'style', etc.

		// Extract page ID from URL (e.g., /Catalog_Page/123/records -> 123)
		const pageMatch = config.url?.match(/Catalog_Page\/(\d+)/);
		const pageId = pageMatch ? pageMatch[1] : (params.page || null);

		// If filtering by page, check if pages metadata exists
		// If not, we can't filter correctly - fall through to network
		if (pageId) {
			const pages = store
				? await store.GetSyncMetadata("pages")
				: await OfflineStore.GetSyncMetadata("pages");
			if (!pages || !Array.isArray(pages)) {
				return null; // Let the request go to network
			}
		}

		try {
			const filterFn = store ? store : OfflineStore;

			const result = await filterFn.FilterPrecachedItems(
				filters,
				search,
				limit,
				offset,
				sort,
				group,
				pageId
			);

			// Transform items to match expected API response format
			// The precache items have { attribs: {...}, filters: {...}, detail_fields: {...} }
			// The API returns a flat item with all fields at top level
			const items = result.items.map(item => this.#transformPrecacheItem(item));

			// Enrich with cached on-demand data (prices, inventory)
			await this.#enrichWithOnDemandData(items, store);

			// Always return result from precache (even if empty) when we have precached data
			// This ensures search/filter results are respected
			return {
				data: items,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
				_total: result.total,
			};
		} catch (e) {
			console.warn("[OfflineInterceptor] Failed to serve precached records:", e);
			return null;
		}
	}

	/**
	 * Serve attribute definitions from cache or build fallback.
	 * The /attributes/details endpoint returns attribute display definitions, not item data.
	 * Used for offline product detail viewing.
	 */
	static async #servePrecachedDetails(config, store) {
		// First, try to get cached attribute definitions from the original request cache
		const key = this.#getDedupKey(config);
		const s = store || OfflineStore;
		const cached = await s.GetCachedResponseWithMeta(key, Infinity);

		if (cached?.data) {
			return {
				data: cached.data,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
			};
		}

		// Try to get fields from precache (new enhanced structure)
		const fieldDefs = await s.GetFieldDefsByContext("detail");

		if (fieldDefs && fieldDefs.length > 0) {
			// Transform fields to attribute format expected by ProductAttributes
			// Spread the full def to preserve all properties (currency, prefix, suffix, etc.)
			const attributes = fieldDefs.map(def => ({
				...def,
				// Respect the original show_label value; only default to true if undefined
				show_label: def.show_label ?? true,
				type: def.type || def.format_type || 'text',
				show_variations: def.show_variations || false,
				values: def.values || [],
			}));

			return {
				data: attributes,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
			};
		}

		// No cached definitions - build fallback from precache item structure

		try {
			const { items } = await s.GetPrecachedItems();
			if (items.length === 0) {
				return null;
			}

			// Get sample item to determine available attributes
			const sampleItem = items[0].attribs || items[0];
			// Also check detail_fields for additional attributes
			const detailFields = sampleItem.detail_fields || {};

			// Helper to check if a value is a simple renderable type
			const isRenderableValue = (value) => {
				if (value === null || value === undefined) return true;
				const type = typeof value;
				// Only allow primitives that React can render
				return type === 'string' || type === 'number' || type === 'boolean';
			};

			// Fields to always exclude (internal/complex objects that shouldn't be displayed)
			const excludedFields = [
				'id', 'filters', 'created_at', 'updated_at', 'deleted_at',
				'media', 'images', 'variations', 'options', 'metadata',
				'attributes', 'relationships', 'links', 'embeds',
				// Internal/system fields that shouldn't be displayed
				'handle', 'grouping', 'siblings', 'siblings_by_family',
				'html', 'stone', 'count', 'group', 'detail_fields',
				// Image fields (handled separately by media)
				'image_0', 'image_1', 'image_2', 'image_3', 'image_4',
			];

			// Combine keys from both attribs and detail_fields
			const allKeys = new Set([
				...Object.keys(sampleItem),
				...Object.keys(detailFields)
			]);

			const attributeKeys = Array.from(allKeys).filter(key => {
				// Filter out internal/system fields
				if (key.startsWith('_')) return false;
				if (key.startsWith('data:')) return false; // Internal data fields
				if (excludedFields.includes(key)) return false;

				// Check if the value is a renderable type (not an object or array)
				const value = sampleItem[key] ?? detailFields[key];
				if (!isRenderableValue(value)) {
					return false;
				}

				return true;
			});

			// Build basic attribute definitions from the available keys
			const fallbackAttributes = attributeKeys.map(key => {
				const label = key
					.replace(/_/g, ' ')
					.replace(/\b\w/g, c => c.toUpperCase());

				// Detect common types
				let type = 'text';
				if (key.includes('price') || key.includes('cost')) {
					type = 'currency';
				} else if (key.includes('date')) {
					type = 'date';
				}

				return {
					reference: key,
					label,
					show_label: true,
					type,
				};
			});

			// Prioritize common attributes at the top
			const priority = ['name', 'title', 'sku', 'common_sku', 'retail_price', 'upc', 'material', 'description'];
			fallbackAttributes.sort((a, b) => {
				const aIndex = priority.indexOf(a.reference);
				const bIndex = priority.indexOf(b.reference);
				if (aIndex === -1 && bIndex === -1) return 0;
				if (aIndex === -1) return 1;
				if (bIndex === -1) return -1;
				return aIndex - bIndex;
			});

			return {
				data: fallbackAttributes,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
				_fallbackAttributes: true,
			};
		} catch (e) {
			console.warn("[OfflineInterceptor] Failed to build fallback attributes:", e);
		}

		return null;
	}

	/**
	 * Serve gallery/thumbnail attribute definitions from cache or build fallback.
	 * The /attributes endpoint returns attribute definitions for the gallery view.
	 * Used for offline gallery thumbnail display.
	 */
	static async #servePrecachedGalleryAttributes(config, store) {
		// First, try to get cached attribute definitions
		const key = this.#getDedupKey(config);
		const s = store || OfflineStore;
		const cached = await s.GetCachedResponseWithMeta(key, Infinity);

		if (cached?.data) {
			return {
				data: cached.data,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
			};
		}

		// Try to get fields from precache with thumb context
		const fieldDefs = await s.GetFieldDefsByContext("thumb");

		if (fieldDefs && fieldDefs.length > 0) {
			// Transform fields to attribute format expected by gallery
			// Spread the full def to preserve all properties (currency, prefix, suffix, position, etc.)
			const attributes = fieldDefs.map(def => ({
				...def,
				// Respect the original show_label value; default to false for gallery thumbnails
				show_label: def.show_label ?? false,
				type: def.type || def.format_type || 'text',
			}));

			return {
				data: attributes,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
			};
		}

		// No cached definitions - build fallback from precache item structure
		// Gallery attributes are typically a subset focused on display in thumbnails

		try {
			const { items } = await s.GetPrecachedItems();
			if (items.length === 0) {
				return null;
			}

			// Get sample item to determine available attributes
			const sampleItem = items[0].attribs || items[0];

			// Helper to check if a value is a simple renderable type
			const isRenderableValue = (value) => {
				if (value === null || value === undefined) return true;
				const type = typeof value;
				return type === 'string' || type === 'number' || type === 'boolean';
			};

			// Fields to exclude from gallery view (internal/complex fields)
			const excludedFields = [
				'id', 'filters', 'created_at', 'updated_at', 'deleted_at',
				'media', 'images', 'variations', 'options', 'metadata',
				'attributes', 'relationships', 'links', 'embeds',
				'handle', 'grouping', 'siblings', 'siblings_by_family',
				'html', 'stone', 'image_0', 'image_1', 'image_2', 'image_3',
				'count', 'unit_price', 'cost', 'wholesale_price',
			];

			const attributeKeys = Object.keys(sampleItem).filter(key => {
				if (key.startsWith('_')) return false;
				if (key.startsWith('data:')) return false;
				if (excludedFields.includes(key)) return false;
				const value = sampleItem[key];
				if (!isRenderableValue(value)) return false;
				return true;
			});

			// Gallery typically shows: name/title, SKU, price, maybe one more attribute
			// Prioritize common gallery fields - thumbnails typically don't show labels
			const galleryPriority = ['common_sku', 'sku', 'retail_price', 'description', 'name', 'title', 'category'];

			// Build gallery attribute definitions (limited set for thumbnails)
			// Gallery/thumbnail view typically shows values without labels for compact display
			const galleryAttributes = [];

			for (const priorityKey of galleryPriority) {
				if (attributeKeys.includes(priorityKey)) {
					const label = priorityKey
						.replace(/_/g, ' ')
						.replace(/\b\w/g, c => c.toUpperCase());

					let type = 'text';
					if (priorityKey.includes('price') || priorityKey.includes('cost')) {
						type = 'currency';
					}

					galleryAttributes.push({
						reference: priorityKey,
						label,
						show_label: false, // Gallery thumbnails don't show labels, just values
						type,
					});

					// Limit gallery attributes to key fields (sku, price, description)
					if (galleryAttributes.length >= 3) break;
				}
			}

			return {
				data: galleryAttributes,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
				_fallbackAttributes: true,
			};
		} catch (e) {
			console.warn("[OfflineInterceptor] Failed to build fallback gallery attributes:", e);
		}

		return null;
	}

	/**
	 * Serve filter definitions from precache with dynamically calculated counts.
	 * Counts are computed based on how many precached items match each filter option.
	 */
	static async #servePrecachedFilters(config, store, filterDefsOverride = null) {
		const s = store || OfflineStore;
		// Use override if provided (e.g. from #onSuccess with fresh API data),
		// otherwise read from IndexedDB cache
		const cachedFilters = filterDefsOverride || await s.GetCachedFilters();
		if (!cachedFilters || cachedFilters.length === 0) {
			return null;
		}

		// Check if cached filters have values arrays (option IDs with labels).
		// The precache filter_defs may only contain definitions (id, label, display_type)
		// without the values/options needed for UI rendering and count calculation.
		// If values are missing, return null to let the request go to the API,
		// which will cache the full response via #onSuccess -> CacheFilters.
		const hasValues = cachedFilters.some(f => Array.isArray(f.values) && f.values.length > 0);
		if (!hasValues) {
			return null;
		}

		// Scope to current page if page ID is in the URL
		const pageMatch = config.url?.match(/Catalog_Page\/(\d+)/);
		const pageId = pageMatch ? pageMatch[1] : null;

		// Parse active filter selections from query params (for cascading count updates)
		const activeFilters = config.params?.filter ? JSON.parse(config.params.filter) : [];

		// Search term may be included on the request (aligns counts/greying with the gallery search).
		const searchText = (config.params?.q ?? config.params?.search ?? config.params?.term ?? '').toString();

		// ── PrecacheIndex fast path ──────────────────────────────────
		// Uses inverted indexes for O(1) filter counting instead of loading all items
		const catalogId = s.catalogId;
		if (catalogId) {
			try {
				const precacheIndex = PrecacheIndex.ForCatalog(catalogId);
				if (await precacheIndex.isReady()) {
					const referenceMap = await s.GetFilterReferenceMap() || {};

					// Baseline scope: page only (no filters, no search) — for visibility
					const baselineScopeIds = await precacheIndex.getMatchingItemIds([], pageId, referenceMap);

					// If page-scoped and no items found, fall through to brute-force
					if (baselineScopeIds && baselineScopeIds.size === 0) {
						// Fall through to legacy path which handles this case
					} else {
						// Count scope: page + search narrowing — for greying
						let countScopeIds = baselineScopeIds;
						if (searchText && searchText.trim()) {
							const searchResults = await precacheIndex.searchItems(searchText, baselineScopeIds);
							if (searchResults) {
								countScopeIds = new Set(searchResults);
							}
						}

						// RULE 1 (visibility): baseline counts with no active filters
						const baselineFiltersWithCounts = await precacheIndex.getFilterCounts(
							cachedFilters, baselineScopeIds, [], referenceMap
						);

						// RULE 2 (greying): counts with search + other active filters (cascading)
						const filtersWithCounts = await precacheIndex.getFilterCounts(
							cachedFilters, countScopeIds, activeFilters, referenceMap
						);

						if (baselineFiltersWithCounts && filtersWithCounts) {
							// Prune + assemble result using same visibility/greying rules
							const prunedResult = this.#pruneFilterCounts(
								filtersWithCounts, baselineFiltersWithCounts
							);
							return {
								data: prunedResult,
								status: 200,
								statusText: "OK",
								headers: {},
								config,
								_fromCache: true,
								_fromPrecache: true,
							};
						}
					}
				}
			} catch (e) {
				console.warn("[OfflineInterceptor] PrecacheIndex filter fast path failed, falling back:", e);
			}
		}

		// ── Legacy brute-force path ──────────────────────────────────
		// Loads all items into memory — used when PrecacheIndex is not ready
		// Guard against OOM for large catalogs
		const itemCount = await s.GetPrecacheItemCount();
		if (itemCount > 50000) {
			console.warn(`[OfflineInterceptor] ${itemCount} items too large for brute-force filter counts`);
			return null;
		}
		const { items: allItems } = await s.GetPrecachedItems();

		let scopedItems = allItems;
		if (pageId) {
			const pages = await s.GetSyncMetadata("pages");
			if (pages && Array.isArray(pages)) {
				const numericPageId = parseInt(pageId, 10);
				const page = pages.find(p =>
					p.id === numericPageId || p.id === String(pageId) || String(p.id) === String(pageId)
				);
				if (page && Array.isArray(page.items)) {
					const pageItemIds = new Set(page.items.map(id => Number(id)));
					scopedItems = allItems.filter(item => {
						const itemId = item.attribs?.id;
						return itemId && pageItemIds.has(Number(itemId));
					});
				}
			}
		}
		if (scopedItems.length === 0) {
			return null;
		}

		// RULE 1 (visibility): baseline counts — page scoped, no active filters
		const baselineFiltersWithCounts = await this.#calculateFilterCounts(cachedFilters, scopedItems, []);

		// RULE 2 (greying): counts with search + active filters
		let countScopeItems = scopedItems;
		if (searchText && searchText.trim() && pageId) {
			try {
				const r = await s.FilterPrecachedItems([], searchText, scopedItems.length || 100000, 0, null, null, pageId);
				if (Array.isArray(r?.items)) countScopeItems = r.items;
			} catch (e) {
				console.warn('[OfflineInterceptor] Failed to apply search scope for /filters:', e);
			}
		} else if (searchText && searchText.trim() && !pageId) {
			try {
				const r = await s.FilterPrecachedItems([], searchText, allItems.length || 100000, 0, null, null, null);
				if (Array.isArray(r?.items)) countScopeItems = r.items;
			} catch (e) {
				console.warn('[OfflineInterceptor] Failed to apply search scope for /filters (no pageId):', e);
			}
		}

		const filtersWithCounts = await this.#calculateFilterCounts(cachedFilters, countScopeItems, activeFilters);

		// Prune and assemble
		const prunedResult = this.#pruneFilterCounts(filtersWithCounts, baselineFiltersWithCounts);
		return {
			data: prunedResult,
			status: 200,
			statusText: "OK",
			headers: {},
			config,
			_fromCache: true,
			_fromPrecache: true,
		};
	}

	/**
	 * Prune filter counts based on baseline visibility.
	 * Removes options that have zero count in the baseline (page-scoped, no filters).
	 * Preserves tree/hierarchy ancestors for hierarchical filter types.
	 * @param {Array} filtersWithCounts - Filters with active counts
	 * @param {Array} baselineFiltersWithCounts - Filters with baseline (visibility) counts
	 * @returns {Array} Pruned filters
	 */
	static #pruneFilterCounts(filtersWithCounts, baselineFiltersWithCounts) {
		const normalizeFilterId = (f) => {
			if (!f) return null;
			const idStr = (typeof f.id === 'object' && f.id?.common !== undefined)
				? `c.${f.id.common}`
				: (f.id != null ? String(f.id) : null);
			return {
				reference: f.reference != null ? String(f.reference) : null,
				property: f.property != null ? String(f.property) : null,
				id: idStr,
			};
		};

		const baselineByKey = new Map();
		for (const bf of baselineFiltersWithCounts || []) {
			const ids = normalizeFilterId(bf);
			for (const k of [ids?.reference, ids?.property, ids?.id].filter(Boolean)) {
				if (!baselineByKey.has(k)) baselineByKey.set(k, bf);
			}
		}

		const keepOptionIdsForBaseline = (baselineFilter) => {
			const values = baselineFilter?.values || [];
			const byId = new Map(values.map(v => [String(v?.id).trim(), v]));
			const keep = new Set();
			for (const opt of values) {
				if (!(+opt?.count > 0)) continue;
				let id = String(opt.id).trim();
				if (!id) continue;
				keep.add(id);
				if (["tree", "multiselect"].includes(String(baselineFilter?.display_type || baselineFilter?.type || '').toLowerCase())) {
					let parent = opt.parent;
					const visited = new Set();
					while (parent !== undefined && parent !== null && parent !== 0 && parent !== '0') {
						const pid = String(parent).trim();
						if (!pid || visited.has(pid)) break;
						visited.add(pid);
						keep.add(pid);
						const parentNode = byId.get(pid);
						if (!parentNode) break;
						parent = parentNode.parent;
					}
				}
			}
			return keep;
		};

		for (const f of (filtersWithCounts || [])) {
			if (!Array.isArray(f.values)) continue;
			const ids = normalizeFilterId(f);
			const baseline = [ids?.reference, ids?.property, ids?.id]
				.filter(Boolean)
				.map(k => baselineByKey.get(k))
				.find(Boolean);
			if (!baseline) continue;
			const keep = keepOptionIdsForBaseline(baseline);
			f.values = f.values.filter(opt => keep.has(String(opt.id).trim()));
		}

		return (filtersWithCounts || []).filter(f => Array.isArray(f.values) && f.values.length > 0);
	}

	/**
	 * Calculate counts for each filter option based on precached items.
	 * Supports cascading counts: when activeFilters are provided, each filter's
	 * counts reflect items matching all OTHER active filters (not its own).
	 * Supports both array-format and object-format item.filters.
	 * @param {Array} filterDefs - Filter definitions with values/options
	 * @param {Array} allItems - All items to count against
	 * @param {Array} activeFilters - Active filter selections [{property, value}]
	 */
	static async #calculateFilterCounts(filterDefs, allItems, activeFilters = []) {

		const coerceFilterValueKey = (raw) => {
			if (raw === undefined || raw === null) return null;
			if (typeof raw === 'boolean') return raw ? 'true' : 'false';
			if (typeof raw === 'object') {
				if (raw.id !== undefined && raw.id !== null) return raw.id;
				if (raw.value !== undefined && raw.value !== null) return raw.value;
				if (raw.common !== undefined && raw.common !== null) return raw.common;
				if (raw.label !== undefined && raw.label !== null) {
					return String(raw.label).replace(/<[^>]*>/g, '').trim();
				}
				if (raw.name !== undefined && raw.name !== null) {
					return String(raw.name).replace(/<[^>]*>/g, '').trim();
				}
				return null;
			}
			return raw;
		};


		// Normalize activeFilters so downstream logic can treat them consistently.
		// We may receive any of:
		// - [{ property: 'c.6', value: [...] }]
		// - [{ filterId: 'c.6', value: [...] }] (from record filtering paths)
		// - [[ 'c.6', [ ... ]]] (legacy tuple format)
		const normalizedActiveFilters = (Array.isArray(activeFilters) ? activeFilters : [])
			.map((af) => {
				if (!af) return null;
				if (Array.isArray(af)) {
					const [property, value] = af;
					return { property, value };
				}
				const property = af.property ?? af.filterId ?? af.filter_id ?? af.id;
				const value = af.value ?? af.values;
				return { ...af, property, value };
			})
			.filter(Boolean);

		const activeFilterKey = (af) => String(af?.property ?? '');

		// Create a deep copy to avoid mutating cached data
		const filters = JSON.parse(JSON.stringify(filterDefs));

		// Normalize filter structure: convert object filter.id to string using reference
		for (const filter of filters) {
			if (filter.id && typeof filter.id === 'object' && filter.id.common !== undefined) {
				// Use reference as the canonical ID (e.g., "c.35")
				filter.id = filter.reference || `c.${filter.id.common}`;
			}
			// Ensure display_type is set
			if (filter.type && !filter.display_type) {
				filter.display_type = filter.type;
			}
		}


		// Detect item.filters format: array [1,2,3] vs object {"1126": [89,90]}
		const sampleItem = allItems[0];
		const sampleFilters = sampleItem?.filters;
		const isObjectFormat = sampleFilters && !Array.isArray(sampleFilters) && typeof sampleFilters === 'object';

		// Build referenceMap for object-format: maps UI filter IDs ("c.35") to precache keys ("1126")
		let referenceMap = {};
		if (isObjectFormat) {
			const catalogId = OfflineStore.GetCurrentCatalogId?.();
			if (catalogId) {
				const store = OfflineStore.ForCatalog(catalogId);
				referenceMap = await store.GetFilterReferenceMap() || {};
			}
		}

		// Helper: resolve the item attribute value for a filter's range options
		const resolveRangeValue = (item, filter) => {
			const attribs = item.attribs || item;
			const detailFields = attribs.detail_fields || item.detail_fields || {};
			const keys = [filter.reference, filter.property, filter.id].filter(Boolean);
			for (const key of keys) {
				const v = attribs[key] ?? detailFields[key];
				if (v != null) return v;
			}
			const label = (filter.label || '').toLowerCase().replace(/\s*range\s*/g, '').trim();
			const attrKey = label.replace(/\s+/g, '_');
			const v = attribs[attrKey] ?? detailFields[attrKey];
			if (v != null) return v;
			if (label.includes('price') || label.includes('cost')) {
				for (const pk of ['retail_price', 'price', 'wholesale_price', 'cost', 'unit_price']) {
					const pv = attribs[pk] ?? detailFields[pk];
					if (pv != null) return pv;
				}
			}
			return undefined;
		};

		// Helper: check if an item matches a single active filter selection
		const itemMatchesFilter = (item, af, filterDef) => {
			if (!af.value || (Array.isArray(af.value) && !af.value.length)) return true;
			const itemFilters = item.filters || [];
			const selectedValues = Array.isArray(af.value) ? af.value : [af.value];

			for (const sv of selectedValues) {
				const svStr = String(sv);

				if (isObjectFormat) {
					// Object format: item.filters = { "c.153": [89, 90] }
					// af.property is the UI filter ID (e.g., "c.35")
					// Map via referenceMap only if the original key does not exist.
					// Many catalogs already store item.filters keys as "c.<id>"; blindly mapping
					// would change "c.35" -> "1126" and incorrectly miss matches.
					let filterKey = String(af.property);
					let itemValue = itemFilters[filterKey];
					if (itemValue === undefined) {
						const mappedKey = referenceMap[filterKey];
						if (mappedKey !== undefined) {
							filterKey = String(mappedKey);
							itemValue = itemFilters[filterKey];
						}
					}

					if (Array.isArray(itemValue)) {
						for (const v of itemValue) {
							const k = coerceFilterValueKey(v);
							if (k === null) continue;
							const ks = String(k);
							if (ks === svStr) return true;
							if (/\D/.test(ks) && !ks.includes(':') && ks.trim().toLowerCase() === svStr.trim().toLowerCase()) return true;
						}
					} else if (typeof itemValue === 'boolean') {
						if (itemValue) return true;
					} else if (itemValue !== undefined) {
						const k = coerceFilterValueKey(itemValue);
						if (k !== null) {
							const ks = String(k);
							if (ks === svStr) return true;
							if (/\D/.test(ks) && !ks.includes(':') && ks.trim().toLowerCase() === svStr.trim().toLowerCase()) return true;
						}
					}
				} else {
					// Array format: item.filters = [1126, 1, 0]
					if (Array.isArray(itemFilters) && itemFilters.some(f => String(f) === svStr)) {
						return true;
					}
				}

				// Fall back to range resolution for range-type values
				if (svStr.includes(':')) {
					const parts = svStr.split(':');
					const min = parseFloat(parts[0]);
					const max = parts[1] ? parseFloat(parts[1]) : Infinity;
					if (!isNaN(min)) {
						const rawVal = resolveRangeValue(item, filterDef || {});
						if (rawVal != null) {
							const val = parseFloat(String(rawVal).replace(/[^0-9.-]/g, ''));
							if (!isNaN(val) && val >= min && val <= max) return true;
						}
					}
				}
			}
			return false;
		};

		// Build a map from property -> filter definition for matching
		const filterDefMap = new Map();
		for (const f of filters) {
			const ref = f.reference || f.property;
			if (ref) filterDefMap.set(String(ref), f);
			// Handle object-format filter.id: {common: 35, options: []}
			if (f.id) {
				const idStr = typeof f.id === 'object' && f.id?.common !== undefined
					? `c.${f.id.common}`
					: String(f.id);
				filterDefMap.set(idStr, f);
			}
		}

		// For each filter, compute counts using items that match all OTHER active filters
		for (const filter of filters) {
			if (!filter.values || !Array.isArray(filter.values)) continue;

			// All possible identifiers for this filter (reference, property, id)
			// Handle object-format filter.id: {common: 35, options: []}
			const idStr = typeof filter.id === 'object' && filter.id?.common !== undefined
				? `c.${filter.id.common}`
				: (filter.id ? String(filter.id) : null);
			const filterIds = new Set(
				[filter.reference, filter.property, idStr].filter(Boolean)
			);

			// Get the subset of items matching all active filters EXCEPT this one
			const otherFilters = normalizedActiveFilters.filter(af => {
				const key = activeFilterKey(af);
				const value = af.value;
				return !filterIds.has(key)
					&& value && (!Array.isArray(value) || value.length);
			});
			let items = allItems;
			if (otherFilters.length > 0) {
				items = allItems.filter(item =>
					otherFilters.every(af => itemMatchesFilter(item, af, filterDefMap.get(activeFilterKey(af))))
				);
			}

			// Build count map for this filter's item subset
			const optionCounts = new Map();

			const addCount = (rawKey) => {
				if (rawKey === undefined || rawKey === null || rawKey === '') return;
				const key = String(rawKey);
				optionCounts.set(key, (optionCounts.get(key) || 0) + 1);
				// Also store a normalized key for non-numeric strings to handle
				// backend variations in casing/whitespace.
				if (/\D/.test(key) && !key.includes(':')) {
					const norm = key.trim().toLowerCase();
					if (norm && norm !== key) {
						optionCounts.set(norm, (optionCounts.get(norm) || 0) + 1);
					}
				}
			};

			if (isObjectFormat) {
				// Object format: item.filters = { "c.153": [89, 90] }
				// The item.filters keys match filter.reference format (both use "c.<id>")
				// So we prefer filter.reference for the lookup key
				const sampleItem = items[0];

				// Build the filter key, preferring reference since it matches item.filters format
				let filterKey = filter.reference;

				// If no reference, try to build from object id or string id
				if (!filterKey) {
					if (typeof filter.id === 'object' && filter.id?.common !== undefined) {
						filterKey = `c.${filter.id.common}`;
					} else {
						filterKey = String(filter.id);
					}
				}

				// Check if filterKey exists in the sample item's filters
				// If not, try referenceMap or other fallbacks
				if (sampleItem?.filters && sampleItem.filters[filterKey] === undefined) {
					// Try referenceMap
					if (referenceMap[filterKey] && sampleItem.filters[referenceMap[filterKey]] !== undefined) {
						filterKey = referenceMap[filterKey];
					}
					// Try filter.id directly if it's different from reference
					else if (filter.id && filter.id !== filter.reference && sampleItem.filters[String(filter.id)] !== undefined) {
						filterKey = String(filter.id);
					}
					// Try numeric extraction from reference
					else if (filter.reference) {
						const numericMatch = filter.reference.match(/c\.(\d+)/);
						if (numericMatch && sampleItem.filters[numericMatch[1]] !== undefined) {
							filterKey = numericMatch[1];
						}
					}
				}


				for (const item of items) {
					const itemFilters = item.filters || {};
					const itemValue = itemFilters[filterKey];

					if (Array.isArray(itemValue)) {
						for (const optionId of itemValue) {
							const k = coerceFilterValueKey(optionId);
							if (k !== null) addCount(k);
						}
					} else if (typeof itemValue === 'boolean' && itemValue) {
						// Toggle filter: count as "true"
						addCount("true");
					} else if (itemValue !== undefined && itemValue !== null && itemValue !== '') {
						// Some catalogs store a single option id (scalar) instead of an array.
						const k = coerceFilterValueKey(itemValue);
						if (k !== null) addCount(k);
					}
				}
			} else {
				// Array format: item.filters = [1126, 1, 0]
				for (const item of items) {
					const itemFilters = item.filters || [];
					if (Array.isArray(itemFilters)) {
						for (const optionId of itemFilters) {
							addCount(optionId);
						}
					}
				}
			}


			for (const option of filter.values) {
				const optionId = String(option.id).trim();
				const optionValue = String(option.value ?? '').trim();
				const optionLabelNorm = String(option.label ?? '').replace(/<[^>]*>/g, '').trim().toLowerCase();

				// Range-type option
				if (optionId.includes(':')) {
					// First check if items already have this range ID in their filters array
					const directCount = optionCounts.get(optionId) || 0;
					if (directCount > 0) {
						option.count = directCount;
						continue;
					}

					// Fall back to resolving numeric value from item attributes
					const parts = optionId.split(':');
					const min = parseFloat(parts[0]);
					const max = parts[1] ? parseFloat(parts[1]) : Infinity;

					if (!isNaN(min)) {
						let count = 0;
						for (const item of items) {
							const rawVal = resolveRangeValue(item, filter);
							if (rawVal == null) continue;
							const val = parseFloat(String(rawVal).replace(/[^0-9.-]/g, ''));
							if (!isNaN(val) && val >= min && val <= max) {
								count++;
							}
						}
						option.count = count;
						continue;
					}
				}

				// Standard ID-based matching
				let count = optionCounts.get(optionId) || 0;
				if (count === 0 && optionValue) {
					count = optionCounts.get(optionValue) || 0;
					if (count === 0 && /\D/.test(optionValue) && !optionValue.includes(':')) {
						count = optionCounts.get(optionValue.trim().toLowerCase()) || 0;
					}
				}
				if (count === 0 && /\D/.test(optionId) && !optionId.includes(':')) {
					count = optionCounts.get(optionId.trim().toLowerCase()) || 0;
				}
				if (count === 0 && optionLabelNorm) {
					count = optionCounts.get(optionLabelNorm) || 0;
				}
				option.count = count;
			}


		}

		return filters;
	}

	/**
	 * Serve assets from precache.
	 * Transforms precached asset metadata to match the expected API response format.
	 * @param {object} config - Axios request config
	 * @param {object} store - OfflineStore instance
	 * @returns {Promise<object|null>} Response object or null if not available
	 */
	static async #servePrecachedAssets(config, store) {
		// Check if this is a single-asset fetch (e.g., Asset modal loading by ID)
		// These requests need full asset.items data (position, style, item details)
		// which the precache doesn't store, so let them go to the network
		if (config.params?.filter) {
			try {
				const filter = typeof config.params.filter === 'string'
					? JSON.parse(config.params.filter)
					: config.params.filter;
				// Check for id filter: { property: '{id}', value: X } or { property: 'id', value: X }
				if (filter.property === '{id}' || filter.property === 'id') {
					// Single asset fetch - return null to let network request through
					// The modal needs full items data with position/style/item details
					return null;
				}
			} catch (e) {
				// Ignore parse errors
			}
		}

		const s = store || OfflineStore;
		const cachedAssets = await s.GetCachedAssets();

		if (!cachedAssets || cachedAssets.length === 0) {
			return null;
		}

		// Parse the filters param to find which item IDs we're filtering by
		// The gallery uses: filters=[[{property: "items.item.id", value: [123, 456]}]]
		let filterItemIds = null;
		if (config.params?.filters) {
			try {
				const filters = typeof config.params.filters === 'string'
					? JSON.parse(config.params.filters)
					: config.params.filters;

				// Look for items.item.id filter
				for (const filterGroup of filters) {
					if (Array.isArray(filterGroup)) {
						for (const filter of filterGroup) {
							if (filter.property === 'items.item.id' && filter.value) {
								filterItemIds = Array.isArray(filter.value) ? filter.value : [filter.value];
								break;
							}
						}
					}
				}
			} catch (e) {
				console.warn('[OfflineInterceptor] Failed to parse asset filters:', e);
			}
		}

		// Filter assets to those that match the requested item IDs
		let filteredAssets = cachedAssets;
		if (filterItemIds && filterItemIds.length > 0) {
			const itemIdSet = new Set(filterItemIds.map(id => Number(id)));
			filteredAssets = cachedAssets.filter(asset => {
				if (!asset.items || !Array.isArray(asset.items)) return false;
				return asset.items.some(itemId => itemIdSet.has(Number(itemId)));
			});
		}

		// Transform assets to match expected format
		// From: { id, type, source, items: [456, 789] }
		// To: { id, image, items: [{ item: { id: 456 } }] }
		const transformedAssets = filteredAssets.map(asset => ({
			id: asset.id,
			image: asset.source,
			type: asset.type,
			items: (asset.items || []).map(itemId => ({
				item: { id: itemId }
			}))
		}));

		return {
			data: transformedAssets,
			status: 200,
			statusText: "OK",
			headers: {},
			config,
			_fromCache: true,
			_fromPrecache: true,
		};
	}

	/**
	 * Serve global search results from precache.
	 * Used for the global search modal (/Item?q=...).
	 * @param {object} config - Axios request config
	 * @returns {Promise<object|null>} Response object or null if not available
	 */
	static async #servePrecachedGlobalSearch(config, store) {
		const searchQuery = config.params?.q || "";
		const limit = parseInt(config.params?.limit) || 36;


		try {
			// Use FilterPrecachedItems with search query (no filters, no page restriction)
			const s = store || OfflineStore;
			const result = await s.FilterPrecachedItems(
				[], // no filters
				searchQuery, // search query
				limit, // limit
				0, // offset
				null, // sort
				null, // group
				null // pageId - null to search across all items
			);

			// Transform items to match the expected API response format
			// The global search expects: { id, common_sku, name, description, media }
			const items = result.items.map(item => {
				const transformed = this.#transformPrecacheItem(item);

				// Build media array if not present
				// Precache items may have image_0, image_1, etc. instead of media array
				if (!transformed.media) {
					const media = [];
					for (let i = 0;i <= 4;i++) {
						const imgKey = `image_${i}`;
						if (transformed[imgKey]) {
							media.push({
								type: 'image',
								source: transformed[imgKey]
							});
						}
					}
					if (media.length > 0) {
						transformed.media = media;
					} else {
						transformed.media = [];
					}
				}

				return transformed;
			});


			return {
				data: items,
				status: 200,
				statusText: "OK",
				headers: {},
				config,
				_fromCache: true,
				_fromPrecache: true,
				_total: result.total,
				_suggestion: result.suggestion || null,
			};
		} catch (e) {
			console.warn("[OfflineInterceptor] Failed to serve precached global search:", e);
			return null;
		}
	}

	/**
	 * Serve a single item by ID from precache.
	 * Used for product detail pages in offline mode.
	 * @param {string} itemId - The item ID to look up
	 * @returns {Promise<object|null>} Response object or null if not found
	 */
	static async #servePrecachedSingleItem(itemId, store) {
		const s = store || OfflineStore;
		try {
			// Use direct key lookup for efficiency (O(1) instead of scanning all items)
			const found = await s.GetPrecachedItemById(itemId);

			if (found) {
				// Transform precache item to API response format
				const itemData = this.#transformPrecacheItem(found);
				// Enrich with cached on-demand data (prices, inventory)
				await this.#enrichWithOnDemandData([itemData], s);
				return {
					data: itemData,
					status: 200,
					statusText: "OK",
					headers: {},
					_fromCache: true,
					_fromPrecache: true,
				};
			}

			// Fallback: scan all items in case the key format doesn't match
			const { items } = await s.GetPrecachedItems();
			if (!items || items.length === 0) {
				return null;
			}

			// Find the item by ID in precached items (handles type coercion)
			const numericId = parseInt(itemId, 10);
			const foundBySearch = items.find(item => {
				const id = item.attribs?.id || item.id;
				return id === numericId || id === itemId || String(id) === String(itemId);
			});

			if (foundBySearch) {
				// Transform precache item to API response format
				const itemData = this.#transformPrecacheItem(foundBySearch);
				// Enrich with cached on-demand data (prices, inventory)
				await this.#enrichWithOnDemandData([itemData], s);
				return {
					data: itemData,
					status: 200,
					statusText: "OK",
					headers: {},
					_fromCache: true,
					_fromPrecache: true,
				};
			}

			return null;
		} catch (e) {
			console.warn(`[OfflineInterceptor] Error looking up single item:`, e);
			return null;
		}
	}

	/**
	 * Transform a precache item to match API response format.
	 * Merges detail_fields into the top-level item and handles media.
	 * @param {object} precacheItem - Item from precache with attribs, detail_fields, etc.
	 * @returns {object} Flattened item matching API format
	 */
	static #transformPrecacheItem(precacheItem) {
		// Start with attribs or the item itself
		const attribs = precacheItem.attribs || precacheItem;
		const itemData = { ...attribs };

		// Merge detail_fields into top level (these contain retail_price, upc, material, etc.)
		if (attribs.detail_fields && typeof attribs.detail_fields === 'object') {
			for (const [key, value] of Object.entries(attribs.detail_fields)) {
				// Only add if not already present at top level
				if (itemData[key] === undefined && value !== null && value !== '') {
					itemData[key] = value;
				}
			}
			// Remove the nested detail_fields object
			delete itemData.detail_fields;
		}

		// Add grouping field from precache group if available
		if (precacheItem.group && !itemData.grouping) {
			// The group field is used for variation grouping
			itemData._precache_group = precacheItem.group;
		}

		// Preserve group member count for "X variations" badge
		if (precacheItem.count != null) {
			itemData.count = precacheItem.count;
		}

		// Ensure retail_price_range falls back to retail_price for ungrouped/single items
		if (itemData.retail_price_range == null && itemData.retail_price != null) {
			itemData.retail_price_range = itemData.retail_price;
		}

		// TODO: replace with on_demand.computed config when backend ships it.
		// Future: { "retail_price_range": { "source": "retail_price", "aggregate": "range" } }
		// Temporary hardcoded USD formatting — API returns pre-formatted strings
		// (e.g. "$5,900 - $6,150") but precache computes raw numbers.
		if (itemData.retail_price_range != null) {
			const fmtPrice = (v) => {
				const n = parseFloat(v);
				if (isNaN(n)) return v;
				return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 });
			};
			const val = itemData.retail_price_range;
			if (typeof val === 'number') {
				itemData.retail_price_range = fmtPrice(val);
			} else if (typeof val === 'string' && !val.includes('$')) {
				if (val.includes(' - ')) {
					itemData.retail_price_range = val.split(' - ').map(fmtPrice).join(' - ');
				} else {
					itemData.retail_price_range = fmtPrice(val);
				}
			}
		}

		return itemData;
	}

	/**
	 * Enrich precache items with on-demand data (prices, inventory) from IDB cache.
	 * Does NOT make network calls — only reads from IDB.
	 * @param {object[]} items - Transformed precache items
	 * @param {object} store - Catalog-bound OfflineStore instance
	 * @returns {Promise<void>}
	 */
	static async #enrichWithOnDemandData(items, store) {
		if (!items?.length) return;

		const s = store || OfflineStore;

		try {
			const onDemand = await s.GetSyncMetadata("on_demand");
			if (!onDemand) return;

			const catalogId = s.catalogId || OfflineStore.GetCurrentCatalogId?.();
			if (!catalogId) return;

			const itemIds = items.map(it => it.id).filter(Boolean);
			if (!itemIds.length) return;

			// Enrich with prices (from IDB only, no network)
			if (onDemand.prices) {
				if (!this.#priceFetcher || this.#priceFetcher.catalogId !== catalogId) {
					this.#priceFetcher = new Item_Price_Fetcher(catalogId, '', '', onDemand.prices);
				}
				await this.#priceFetcher.preloadFromCache(itemIds);
				this.#priceFetcher.enrichItems(items);
			}

			// Enrich with inventory (from IDB only, no network)
			if (onDemand.inventory) {
				if (!this.#inventoryFetcher || this.#inventoryFetcher.catalogId !== catalogId) {
					this.#inventoryFetcher = new Item_Inventory_Fetcher(catalogId, '', '', onDemand.inventory);
				}
				await this.#inventoryFetcher.preloadFromCache(itemIds);
				this.#inventoryFetcher.enrichItems(items);
			}
		} catch (e) {
			console.warn('[OfflineInterceptor] On-demand enrichment failed (non-fatal):', e);
			OfflineErrors.log("enrichWithOnDemandData", e);
		}
	}

	/**
	 * Queue an offline write and return optimistic response.
	 * Used for simulated offline mode to queue mutations without hitting network.
	 */
	static async #queueOfflineWrite(config, model, store) {
		const s = store || OfflineStore;
		await s.QueueMutation(
			model.className,
			config.method,
			config.url,
			config.data,
			config
		);

		const optimistic = this.#buildOptimisticResponse(config.method, config.data);
		config.adapter = () => Promise.resolve({
			data: optimistic,
			status: 200,
			statusText: "OK",
			headers: {},
			config,
			_queued: true,
			_fromPrecache: true,
		});
		return config;
	}

	// Track URLs currently being background-revalidated to prevent duplicate fetches
	static #backgroundRevalidating = new Set();

	/**
	 * Trigger a background revalidation for stale data.
	 * Called when stale-while-revalidate serves stale cache.
	 * @param {string} url
	 * @param {object} config
	 */
	static async #backgroundRevalidate(_url, config) {
		if (!this.#axiosInstance) return;

		const revalidateKey = config.url + JSON.stringify(config.params || {});
		if (this.#backgroundRevalidating.has(revalidateKey)) return;
		this.#backgroundRevalidating.add(revalidateKey);

		try {
			// Make the request without cache-first and bypass precache to force network fetch
			const freshConfig = {
				...config,
				_model: { ...config._model, cacheFirst: false },
				_skipPrecache: true,
			};
			await this.#axiosInstance.request(freshConfig);
		} catch (e) {
			OfflineErrors.log("backgroundRevalidate", e, { url: config.url });
		} finally {
			this.#backgroundRevalidating.delete(revalidateKey);
		}
	}

	/**
	 * Manually invalidate cache for a class.
	 * Useful after mutations that affect list views.
	 * @param {string} className
	 */
	static async InvalidateClass(className) {
		await OfflineStore.ClearClassCache(className);
	}

	/**
	 * Get delta update params if available.
	 * Returns params to append to request for incremental sync.
	 * @param {string} className
	 * @returns {object|null}
	 */
	static GetDeltaParams(className) {
		return OfflineStore.GetDeltaParams(className);
	}
}
